/**
 * **The client card's „Rozliczenie" band** (T-K1) — drawn only while the trainer bills in credits
 * (booking-system R18): where this client stands and since when (R16, `features/credit-ledger`'s
 * one standing rule), and the next session on their calendar.
 *
 * The band is one answer built from three reads, decided here so the card only draws. Whether it
 * stands at all is `creditBandPresence`, which the wiring asks too; the view is:
 * - `off` — no band, not an empty one;
 * - `failed` — the policy says credits run and the summary did not answer;
 * - `ready` — the standing, the ledger's start, and the next session (or none, or unread).
 *
 * Dates are said on the clock the caller names (`zone`) — the reader's own, on the card.
 *
 * Pure: no React, no DOM, no query hook, no i18next — `t`, the locale and the zone arrive as
 * parameters.
 */

import type { TFunction } from 'i18next';

import {
  occurrenceKindKey,
  type OccurrenceKind,
  type OccurrenceStatusKey,
} from '~/entities/calendar-event';
import { creditStanding, creditsRun, type CreditStanding } from '~/features/credit-ledger';

/** One occurrence on the client's calendar, as the band reads it. */
export interface UpcomingSource {
  readonly id: number;
  /**
   * The instant it starts — for one with no hour, the reader's midnight of the day it names —
   * or `null` when the wire places it nowhere readable.
   */
  readonly startMs: number | null;
  /** Whether it stands at an hour. A plan session often names only its day. */
  readonly hasHour: boolean;
  readonly status: OccurrenceStatusKey | null;
  readonly kind: OccurrenceKind;
  /** The plan's own name for the session, when a plan put it there. */
  readonly planSessionName: string | null;
  readonly minutes: number | null;
}

/** The statuses that are still ahead — anything else happened, was called off, or was let go. */
const AHEAD: ReadonlySet<OccurrenceStatusKey | null> = new Set(['scheduled', 'emptySlot', null]);

const DAY_MS = 86_400_000;

/**
 * The next session still ahead of `nowMs`, or `null` when the calendar holds none. One with no
 * hour stays ahead for the whole of the day it names.
 */
export function nextSession(
  sources: readonly UpcomingSource[],
  nowMs: number,
): UpcomingSource | null {
  let next: UpcomingSource | null = null;
  for (const source of sources) {
    if (source.startMs === null || !AHEAD.has(source.status)) continue;
    if ((source.hasHour ? source.startMs : source.startMs + DAY_MS) <= nowMs) continue;
    if (next === null || (next.startMs ?? 0) > source.startMs) next = source;
  }
  return next;
}

export interface SummaryRow {
  readonly customerId: number;
  readonly balance: number;
  readonly sessionsOwed: number;
}

export interface BandPresenceInput {
  readonly customerId: number;
  /**
   * The trainer's booking policy's `creditsEnabled`, read before the screen draws (the route
   * loader) — `null` when the policy did not answer. What the band's wait holds room for, and
   * whether an unanswered summary is an error worth drawing.
   */
  readonly policyOn: boolean | null;
  /** `undefined` while the summary is read; `null` when it did not answer. */
  readonly summary:
    | { readonly creditsEnabled: boolean; readonly clients: readonly SummaryRow[] | null }
    | null
    | undefined;
}

/**
 * **Whether the band stands — decided once**, for the reads it enables, the room its wait holds,
 * and what it draws:
 * - `ready`: the summary says credits run and lists this client (R18, R16);
 * - `reserve`: the summary is still read and the policy says credits run — hold the band's room;
 * - `failed`: the summary did not answer and the policy says credits run (R6 of the fix round);
 * - `none`: everything else — credits off, the client not on the ledger, or nothing known. Never
 *   an error while the policy says off.
 */
export type BandPresence = 'ready' | 'reserve' | 'failed' | 'none';

export function creditBandPresence(input: BandPresenceInput): BandPresence {
  const { summary } = input;
  // `features/credit-ledger`'s one rule: the summary once it answered, the policy before.
  if (creditsRun(input.policyOn, summary ?? null) !== true) return 'none';
  if (summary === undefined) return 'reserve';
  if (summary === null) return 'failed';
  const listed = summary.clients?.some((row) => row.customerId === input.customerId) ?? false;
  return listed ? 'ready' : 'none';
}

export interface CreditBandInput extends BandPresenceInput {
  /** The ledger's start (`ledgerSince`), when its first page has answered. */
  readonly since: string | null;
  /** The client's calendar ahead, or `null` when it did not answer. */
  readonly upcoming: readonly UpcomingSource[] | null;
  readonly nowMs: number;
}

export type NextSessionView =
  | { readonly kind: 'session'; readonly when: string; readonly what: string }
  | { readonly kind: 'none' }
  | { readonly kind: 'failed' };

export type CreditBandView =
  | { readonly kind: 'off' }
  | { readonly kind: 'failed' }
  | {
      readonly kind: 'ready';
      readonly standing: CreditStanding;
      /** The ledger's figures, for the acts to be judged against. */
      readonly balance: number;
      readonly sessionsOwed: number;
      /** „rozliczenie od 1.09.2026", or `null` before a start is known. */
      readonly since: string | null;
      /** „pt 25.09, 10:00" over „Trening z planu · 60 min" — or nothing ahead, or an unread calendar. */
      readonly next: NextSessionView;
    };

function sinceWords(iso: string | null, words: BandWords): string | null {
  const { locale, zone, t } = words;
  if (iso === null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    day: 'numeric',
    month: '2-digit',
    year: 'numeric',
  }).format(at);
  return t('web:booking.credits.ledger.since', { date });
}

function nextWords(session: UpcomingSource, words: BandWords): NextSessionView {
  const { locale, zone, t } = words;
  const at = new Date(session.startMs ?? 0);
  const weekday = new Intl.DateTimeFormat(locale, { timeZone: zone, weekday: 'short' })
    .format(at)
    .replace(/\.$/, '');
  const day = new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    day: 'numeric',
    month: '2-digit',
  }).format(at);
  const clock = new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(at);
  const kind = t(occurrenceKindKey(session));
  return {
    kind: 'session',
    when: session.hasHour ? `${weekday} ${day}, ${clock}` : `${weekday} ${day}`,
    what:
      session.minutes === null
        ? kind
        : `${kind} · ${t('web:booking.detail.minutes', { count: session.minutes })}`,
  };
}

export interface BandWords {
  readonly t: TFunction;
  readonly locale: string;
  /** The clock dates are said on. */
  readonly zone: string;
}

export function creditBandView(input: CreditBandInput, words: BandWords): CreditBandView {
  const presence = creditBandPresence(input);
  if (presence === 'failed') return { kind: 'failed' };
  const row = input.summary?.clients?.find((entry) => entry.customerId === input.customerId);
  if (presence !== 'ready' || row === undefined) return { kind: 'off' };
  const next = input.upcoming === null ? null : nextSession(input.upcoming, input.nowMs);
  return {
    kind: 'ready',
    standing: creditStanding(row),
    balance: row.balance,
    sessionsOwed: row.sessionsOwed,
    since: sinceWords(input.since, words),
    next:
      input.upcoming === null
        ? { kind: 'failed' }
        : next === null
          ? { kind: 'none' }
          : nextWords(next, words),
  };
}
